// Fetch de los productos (variantes) reales de la empresa del user para
// Interfaz V2. Reemplaza al mock `MOCK_USER_PRODUCTS`.
//
// La data vive en dos tablas:
//   • `variantes` — fila por variante de producto (id, nombre, …).
//   • `productos_imagenes` — filas con imágenes asociadas a una variante.
//
// Las imágenes en `productos_imagenes.url` son nombres de archivo (no URLs).
// El path completo en el bucket `productos` es:
//   `<companyId>/<variantId>/opt/<filename>`   (thumbnail)
//   `<companyId>/<variantId>/full/<filename>`  (full size)
// Si `url` empieza con `http`, es URL completa y se usa tal cual.

import { supabase } from '../../lib/supabase';
import { orDeMarca } from '../../utils/filtroDeMarca';
import { fetchInChunks } from '../../utils/supabaseChunkedIn';
import type { MockProductV2 } from './mockData';
import { FILTRO_VIDEO_PG } from '../../utils/filtroContenido';

const PRODUCTOS_BUCKET = 'productos';
const SIGNED_URL_TTL = 3600; // 1h

interface RawImage {
  variante_id: string;
  url: string;
  principal: boolean | null;
}

/**
 * Mapa `varianteId → ISO de la última generación que lo usó`, para el orden
 * "Últimos utilizados" de los catálogos de productos de V2.
 *
 * No hay columna `ultima_vez_utilizada` en `variantes`: el dato se deriva del
 * historial que YA existe en `imagenes_generadas` (donde también caen los
 * videos), vía la función `get_product_last_used` — ver
 * ADR-2026-08-14-orden-ultimos-utilizados-derivado. La agregación va en la base
 * a propósito: devuelve UNA fila por producto en vez de todo el historial, que
 * en las cuentas pesadas son 5000+ filas por cada carga del catálogo.
 *
 * La RLS de `imagenes_generadas` acota a la empresa del que llama (la función
 * es SECURITY INVOKER), así que el `companyId` es para el índice, no un control
 * de acceso.
 *
 * No filtra por `active`: que el cliente haya borrado la imagen resultante no
 * cambia el hecho de que usó el producto.
 */
export async function fetchProductLastUsedMap(
  companyId: string,
): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  const { data, error } = await supabase.rpc('get_product_last_used', {
    p_company_id: companyId,
  });
  if (error) {
    // No es fatal: sin el mapa, "Últimos utilizados" ordena todo como "nunca
    // usado" (= por fecha de subida) en vez de romper el catálogo.
    console.error('[fetchProductLastUsedMap] rpc error:', error);
    return map;
  }
  for (const row of ((data || []) as any[])) {
    const vid = row?.variante_id as string | null;
    if (!vid || !row?.last_used_at) continue;
    map.set(vid, row.last_used_at as string);
  }
  return map;
}

/**
 * Trae las variantes activas de la empresa + la imagen principal de cada
 * una (signed URL). Mantiene el shape de MockProductV2 para que el resto
 * de la UI (pickers, chips, etc.) no necesite cambios.
 */
export async function fetchUserProducts(
  companyId: string,
  brandId?: string | null,
): Promise<MockProductV2[]> {
  // Joineamos `productos!inner` para leer `model_view` (categoría del
  // producto padre — torso/calzado/cuerpo_completo/etc.). Lo usamos en
  // el product picker para sugerir productos según el template elegido.
  // También traemos `brand_id` para poder filtrar por marca activa (mismo
  // criterio que Estudio Virtual): si `brandId` viene, solo devolvemos los
  // productos de esa marca; si es null/undefined, todos los de la empresa.
  let query = supabase
    .from('variantes')
    .select('id, nombre, created_at, product_id, productos!inner(model_view, brand_id)')
    .eq('activo', true)
    .eq('id_company', companyId);
  // Filtramos por marca activa PERO siempre incluimos los productos sin marca
  // (`brand_id IS NULL`): son productos de la empresa que nunca se asignaron a
  // una marca (onboarding viejo, etc.). Sin esto, una empresa con marca default
  // no vería sus productos huérfanos. Ver [[productos-huerfanos-brand-null]].
  if (brandId) {
    query = query.or(`brand_id.eq.${brandId},brand_id.is.null`, {
      referencedTable: 'productos',
    });
  }
  const { data: variants, error: varErr } = await query.order('created_at', {
    ascending: false,
  });
  if (varErr || !variants || variants.length === 0) return [];

  const variantIds = (variants as any[]).map((v) => v.id as string);
  // Fetch en lotes para no exceder el largo de URL del gateway REST con
  // catálogos grandes (devolvía HTTP 400). Ver utils/supabaseChunkedIn.
  // En paralelo, el mapa de "última vez utilizada" (deriva de
  // `imagenes_generadas`) para el orden "Últimos utilizados" de los pickers.
  const sinAnalisis = [
    ...new Set(
      (variants as any[])
        .filter((v) => !(Array.isArray(v.productos) ? v.productos[0]?.model_view : v.productos?.model_view))
        .map((v) => v.product_id as string)
        .filter(Boolean),
    ),
  ];
  const [imagesRaw, lastUsedMap, esperandoAnalisis] = await Promise.all([
    fetchInChunks<RawImage>(variantIds, (chunk) =>
      supabase
        .from('productos_imagenes')
        .select('variante_id, url, principal')
        .eq('id_company', companyId)
        .in('variante_id', chunk),
    ),
    fetchProductLastUsedMap(companyId),
    productosEsperandoAnalisis(sinAnalisis),
  ]);

  // Agrupar imágenes por variante. Preferimos la principal; sino, la
  // primera que aparece (ya viene ordenada por la query, aunque no
  // ordenamos explícitamente — para mejorar, se podría agregar order
  // by orden ASC).
  const byVariant = new Map<string, RawImage>();
  for (const img of ((imagesRaw || []) as any[]) as RawImage[]) {
    const existing = byVariant.get(img.variante_id);
    if (!existing) {
      byVariant.set(img.variante_id, img);
    } else if (img.principal && !existing.principal) {
      byVariant.set(img.variante_id, img);
    }
  }

  // Firmar URLs en paralelo para todas las variantes con imagen.
  const products: MockProductV2[] = await Promise.all(
    (variants as any[]).map(async (v): Promise<MockProductV2> => {
      const img = byVariant.get(v.id);
      let imageUrl = '';
      if (img?.url) {
        if (img.url.startsWith('http')) {
          imageUrl = img.url;
        } else {
          const optPath = `${companyId}/${v.id}/opt/${img.url}`;
          const { data: signed } = await supabase.storage
            .from(PRODUCTOS_BUCKET)
            .createSignedUrl(optPath, SIGNED_URL_TTL);
          if (signed?.signedUrl) {
            imageUrl = signed.signedUrl;
          } else {
            // Fallback al full si el opt no existe.
            const fullPath = `${companyId}/${v.id}/full/${img.url}`;
            const { data: signedFull } = await supabase.storage
              .from(PRODUCTOS_BUCKET)
              .createSignedUrl(fullPath, SIGNED_URL_TTL);
            imageUrl = signedFull?.signedUrl || '';
          }
        }
      }
      // `productos` viene del join — puede ser objeto o array según el
      // cliente. Normalizamos a string, recortamos whitespace y bajamos
      // a lowercase para que el match contra `tipos_producto_sugeridos`
      // sea robusto a typos de carga (algunos rows tienen "torso\n").
      const productosRel = (v as any).productos;
      const rawModelView = Array.isArray(productosRel)
        ? productosRel[0]?.model_view
        : productosRel?.model_view;
      const modelView =
        typeof rawModelView === 'string'
          ? rawModelView.trim().toLowerCase() || null
          : null;
      return {
        id: v.id as string,
        name: v.nombre as string,
        imageUrl,
        modelView,
        createdAt: v.created_at as string,
        lastUsedAt: lastUsedMap.get(v.id as string) ?? null,
        analisisPendiente: esperandoAnalisis.has(v.product_id as string),
      };
    }),
  );
  return products;
}

/**
 * De estos productos, los que esperan su análisis a pedido (tabla `analisis_diferido`, migración 040 del
 * backend; ADR-2026-10-05-analisis-a-pedido). Tolerante: si la tabla no está o la consulta falla, ninguno, y
 * el catálogo se ve como siempre.
 */
let tablaDeAnalisisFalta = false;
async function productosEsperandoAnalisis(productIds: string[]): Promise<Set<string>> {
  // Sin la migración 040 la tabla no existe: se pregunta una vez y no se vuelve a pedir en cada refresco.
  if (tablaDeAnalisisFalta || productIds.length === 0) return new Set();
  try {
    const rows = await fetchInChunks<{ producto_id: string }>(productIds, (chunk) =>
      supabase.from('analisis_diferido').select('producto_id').in('producto_id', chunk),
    );
    return new Set(rows.map((r) => r.producto_id));
  } catch (e) {
    const code = (e as { code?: string } | null)?.code;
    if (code === 'PGRST205' || code === '42P01') tablaDeAnalisisFalta = true;
    return new Set();
  }
}

/**
 * URL firmada de la imagen PRINCIPAL de una variante en tamaño FULL — para
 * usarla como REFERENCIA de generación (ADR-586). El `imageUrl` del catálogo
 * es el thumbnail `opt` de 300px: alcanza para pickers pero es pobre como
 * referencia de fidelidad de producto. Fallback al `opt` si el full no está.
 * Devuelve null si la variante no tiene imagen.
 */
export async function getProductFullImageUrl(
  companyId: string,
  variantId: string,
): Promise<string | null> {
  const { data } = await supabase
    .from('productos_imagenes')
    .select('url, principal, orden')
    .eq('variante_id', variantId)
    .eq('id_company', companyId)
    .order('principal', { ascending: false })
    .order('orden', { ascending: true })
    .limit(1)
    .maybeSingle();
  const fileName = (data as any)?.url as string | undefined;
  if (!fileName) return null;
  if (fileName.startsWith('http')) return fileName;
  const { data: signedFull } = await supabase.storage
    .from(PRODUCTOS_BUCKET)
    .createSignedUrl(`${companyId}/${variantId}/full/${fileName}`, SIGNED_URL_TTL);
  if (signedFull?.signedUrl) return signedFull.signedUrl;
  const { data: signedOpt } = await supabase.storage
    .from(PRODUCTOS_BUCKET)
    .createSignedUrl(`${companyId}/${variantId}/opt/${fileName}`, SIGNED_URL_TTL);
  return signedOpt?.signedUrl || null;
}

/**
 * TODAS las fotos de una variante, firmadas y en el orden de la ficha —
 * ADR-2026-09-08-los-adjuntos-de-la-caja-dicen-que-son-y-se-abren.
 *
 * Es `getProductFullImageUrl` sin el `.limit(1)`: mismo orden (`principal` primero y
 * después `orden`, que es el de la galería en la tienda) y el mismo fallback de `full/`
 * a `opt/` por foto. La usa la vista ampliada de un adjunto de producto, donde el punto
 * es justamente ver las OTRAS fotos y no sólo la portada.
 *
 * Las que no se pueden firmar se caen de la lista en vez de dejar un hueco roto en la
 * tira. Devuelve `[]` si la variante no tiene fotos.
 */
export async function fetchProductImageUrls(
  companyId: string,
  variantId: string,
): Promise<string[]> {
  const { data } = await supabase
    .from('productos_imagenes')
    .select('url, principal, orden')
    .eq('variante_id', variantId)
    .eq('id_company', companyId)
    .order('principal', { ascending: false })
    .order('orden', { ascending: true });
  const fileNames = ((data ?? []) as { url?: string | null }[])
    .map((f) => f.url)
    .filter((u): u is string => !!u);
  if (fileNames.length === 0) return [];

  // Un pedido por tamaño y no uno por foto: son dos vueltas para una ficha entera.
  // Las de Tiendanube ya vienen como URL absoluta y no pasan por Storage.
  const externas = new Map<string, string>();
  const aFirmar: string[] = [];
  for (const nombre of fileNames) {
    if (nombre.startsWith('http')) externas.set(nombre, nombre);
    else aFirmar.push(nombre);
  }
  const firmar = async (tamano: 'full' | 'opt', nombres: string[]) => {
    if (nombres.length === 0) return new Map<string, string>();
    const { data: firmadas } = await supabase.storage
      .from(PRODUCTOS_BUCKET)
      .createSignedUrls(
        nombres.map((n) => `${companyId}/${variantId}/${tamano}/${n}`),
        SIGNED_URL_TTL,
      );
    const out = new Map<string, string>();
    for (const item of firmadas ?? []) {
      if (!item.path || !item.signedUrl) continue;
      // La clave es el nombre del archivo, que es el último segmento del path: así se
      // vuelve a atar con `fileNames` sin depender del orden en que vuelvan las firmas.
      out.set(item.path.split('/').pop() as string, item.signedUrl);
    }
    return out;
  };
  const enFull = await firmar('full', aFirmar);
  const enOpt = await firmar(
    'opt',
    aFirmar.filter((n) => !enFull.has(n)),
  );

  return fileNames.flatMap((n) => {
    const url = externas.get(n) ?? enFull.get(n) ?? enOpt.get(n);
    return url ? [url] : [];
  });
}

/**
 * Igual que `fetchUserProducts` pero filtrando por la marca (`brand_id` del
 * producto) — para el editor de video, que muestra solo los productos de la
 * marca del proyecto, no de toda la cuenta. Reusa el mismo filtro inner-join
 * (`productos!inner(brand_id)`) que ya usa Estudio Virtual en la web.
 *
 * Incluye SIEMPRE los productos sin marca (`brand_id IS NULL`) además de los de
 * la marca pedida: una empresa nunca debe quedar sin ver sus productos
 * huérfanos. Ver [[productos-huerfanos-brand-null]].
 */
export async function fetchUserProductsByBrand(
  companyId: string,
  brandId: string,
): Promise<MockProductV2[]> {
  const { data: variants, error: varErr } = await supabase
    .from('variantes')
    .select('id, nombre, created_at, productos!inner(brand_id)')
    .eq('activo', true)
    .eq('id_company', companyId)
    .or(`brand_id.eq.${brandId},brand_id.is.null`, {
      referencedTable: 'productos',
    })
    .order('created_at', { ascending: false });
  if (varErr)
    console.error('[fetchUserProductsByBrand] variantes query error:', varErr);
  console.log('[fetchUserProductsByBrand]', {
    companyId,
    brandId,
    variantes: variants?.length ?? 0,
  });
  if (varErr || !variants || variants.length === 0) return [];

  const variantIds = (variants as any[]).map((v) => v.id as string);
  // Fetch en lotes para no exceder el largo de URL del gateway REST con
  // catálogos grandes (devolvía HTTP 400). Ver utils/supabaseChunkedIn.
  const imagesRaw = await fetchInChunks<RawImage>(variantIds, (chunk) =>
    supabase
      .from('productos_imagenes')
      .select('variante_id, url, principal')
      .eq('id_company', companyId)
      .in('variante_id', chunk),
  );

  const byVariant = new Map<string, RawImage>();
  for (const img of ((imagesRaw || []) as any[]) as RawImage[]) {
    const existing = byVariant.get(img.variante_id);
    if (!existing) {
      byVariant.set(img.variante_id, img);
    } else if (img.principal && !existing.principal) {
      byVariant.set(img.variante_id, img);
    }
  }

  return Promise.all(
    (variants as any[]).map(async (v): Promise<MockProductV2> => {
      const img = byVariant.get(v.id);
      let imageUrl = '';
      if (img?.url) {
        if (img.url.startsWith('http')) {
          imageUrl = img.url;
        } else {
          const optPath = `${companyId}/${v.id}/opt/${img.url}`;
          const { data: signed } = await supabase.storage
            .from(PRODUCTOS_BUCKET)
            .createSignedUrl(optPath, SIGNED_URL_TTL);
          if (signed?.signedUrl) {
            imageUrl = signed.signedUrl;
          } else {
            const fullPath = `${companyId}/${v.id}/full/${img.url}`;
            const { data: signedFull } = await supabase.storage
              .from(PRODUCTOS_BUCKET)
              .createSignedUrl(fullPath, SIGNED_URL_TTL);
            imageUrl = signedFull?.signedUrl || '';
          }
        }
      }
      return { id: v.id as string, name: v.nombre as string, imageUrl };
    }),
  );
}

const FONDOS_BUCKET = 'fondos_personalizados';

/**
 * Escenas para el editor de video. Combina:
 *  • las escenas/fondos guardados por el cliente (`fondos_personalizados`,
 *    bucket homónimo, por empresa), y
 *  • la biblioteca default de ATLAS (`fondos`), filtrada por el rubro de la
 *    empresa (mismo criterio que Estudio Virtual: `rubros` incluye el rubro o
 *    es `['all']`). `fondos.link_img` ya es una URL directa.
 * Devuelve el shape de MockProductV2 para reusar la grilla del editor.
 */
export async function fetchSceneImages(
  companyId: string,
): Promise<MockProductV2[]> {
  const out: MockProductV2[] = [];

  // 1. Escenas guardadas por el cliente.
  const { data: custom } = await supabase
    .from('fondos_personalizados')
    .select('id, nombre, nombre_archivo, created_at')
    .eq('id_company', companyId)
    .order('created_at', { ascending: false });
  for (const s of ((custom || []) as any[])) {
    let imageUrl = '';
    if (s.nombre_archivo) {
      const { data: signed } = await supabase.storage
        .from(FONDOS_BUCKET)
        .createSignedUrl(`${companyId}/full/${s.nombre_archivo}`, SIGNED_URL_TTL);
      imageUrl = signed?.signedUrl || '';
    }
    if (imageUrl)
      out.push({
        id: `scene-custom-${s.id}`,
        name: (s.nombre as string) || 'Escena',
        imageUrl,
      });
  }

  // 2. Biblioteca default de ATLAS, filtrada por rubro de la empresa.
  let rubro: string | null = null;
  try {
    const { data: comp } = await supabase
      .from('company_profiles')
      .select('rubro')
      .eq('id_company', companyId)
      .single();
    rubro = (comp as any)?.rubro ?? null;
  } catch {
    /* sin rubro → solo se muestran los fondos marcados 'all' */
  }
  const { data: defaults } = await supabase
    .from('fondos')
    .select('id, link_img, nombre, rubros');
  for (const f of ((defaults || []) as any[])) {
    const arr = (f?.rubros as string[] | undefined) || [];
    const ok =
      (arr.length === 1 && arr[0] === 'all') || (rubro ? arr.includes(rubro) : false);
    if (!ok || !f.link_img) continue;
    out.push({
      id: `scene-fondo-${f.id}`,
      name: (f.nombre as string) || 'Escena',
      imageUrl: f.link_img as string,
    });
  }

  return out;
}

const GENERADAS_BUCKET = 'generadas';

/** Un video generado con ATLAS, para la grilla de "Creaciones" del editor. */
export interface HistorialVideo {
  id: string;
  name: string;
  /** El .mp4 firmado (bucket `generadas`, carpeta `full/`). */
  videoUrl: string;
  /** El poster .png de `opt/`, si existe. Puede venir vacío. */
  thumbUrl: string;
}

/**
 * Historial de VIDEOS generados por el cliente con ATLAS, filtrado por marca.
 *
 * Los videos de la plataforma viven en la MISMA tabla que las imágenes
 * (`imagenes_generadas`) — `fetchHistorialImages` los descarta filtrando por
 * extensión de imagen. La regla para reconocerlos es `FILTRO_VIDEO_PG`, la
 * misma que usa la columna de resultados del Creador de video: por `tipo`, con
 * la extensión como respaldo para las filas viejas que no lo guardaron.
 *
 * El vínculo con la marca va por la **columna `brand_id`** y no por
 * `variante_id → producto → brand_id` como en las imágenes: cubre lo mismo o un
 * poco más en los meses recientes y no obliga a que el video haya salido de un
 * producto. Y va con el MISMO `orDeMarca` que el historial de imágenes — "de
 * esta marca" incluye lo que no tiene marca
 * (ADR-2026-09-13-las-generaciones-sin-marca-se-ven-en-todas). Con `brand_id`
 * exacto, los videos viejos y los de cuentas sin marcas no aparecían en ninguna
 * marca: quedaban fuera de Creaciones del editor aunque la grilla de imágenes
 * de al lado sí los mostraba.
 *
 * El archivo está en `full/<file_name>`; el poster, en `opt/` con el mismo
 * nombre pero `.png` (la convención que ya usa el historial de la plataforma).
 */
export async function fetchHistorialVideos(
  companyId: string,
  brandId: string,
  limit = 40,
): Promise<HistorialVideo[]> {
  const { data, error } = await supabase
    .from('imagenes_generadas')
    .select('id, file_name, prompt, created_at')
    .eq('id_company', companyId)
    .eq('active', true)
    // La marca O sin marca, igual que `fetchHistorialImages`. Los dos `.or()`
    // se combinan con AND (mismo par que usa `useCreacionesRecientes`).
    .or(orDeMarca(brandId))
    .or(FILTRO_VIDEO_PG)
    .not('file_name', 'is', null)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) console.error('[fetchHistorialVideos] query error:', error);
  if (error || !data) return [];

  const filas = (data as any[]).filter((r) =>
    /\.(mp4|webm|mov)$/i.test(r.file_name as string),
  );
  return Promise.all(
    filas.map(async (r): Promise<HistorialVideo> => {
      const fileName = r.file_name as string;
      const posterName = fileName.replace(/\.(mp4|webm|mov)$/i, '.png');
      const [full, opt] = await Promise.all([
        supabase.storage
          .from(GENERADAS_BUCKET)
          .createSignedUrl(`${companyId}/full/${fileName}`, SIGNED_URL_TTL),
        supabase.storage
          .from(GENERADAS_BUCKET)
          .createSignedUrl(`${companyId}/opt/${posterName}`, SIGNED_URL_TTL),
      ]);
      return {
        id: `histvid-${r.id}`,
        name: (r.prompt as string | null)?.slice(0, 80) || 'Video generado',
        videoUrl: full.data?.signedUrl || '',
        thumbUrl: opt.data?.signedUrl || '',
      };
    }),
  );
}

/**
 * Historial de imágenes generadas por el cliente con ATLAS (Estudio Virtual),
 * filtrado por la marca del proyecto. El vínculo con la marca es la columna
 * `brand_id` de la fila (ADR-2026-09-13-brand-id-en-imagenes-generadas; antes se
 * deducía por el producto y las generaciones sin variante no aparecían nunca).
 * Las generaciones sin marca se ven desde todas las marcas, como los productos
 * huérfanos (ADR-2026-09-13-las-generaciones-sin-marca-se-ven-en-todas). Solo imágenes (filtra por extensión),
 * activas, más recientes primero. Devuelve el shape de MockProductV2.
 */
export async function fetchHistorialImages(
  companyId: string,
  brandId: string,
): Promise<MockProductV2[]> {
  const { data, error } = await supabase
    .from('imagenes_generadas')
    .select('id, file_name, created_at')
    .eq('id_company', companyId)
    .eq('active', true)
    // La marca O sin marca (ADR-2026-09-13-las-generaciones-sin-marca-se-ven-en-todas).
    .or(orDeMarca(brandId))
    .order('created_at', { ascending: false })
    .limit(80);
  if (error)
    console.error('[fetchHistorialImages] query error:', error);
  if (error || !data) return [];

  const imgs = (data as any[]).filter(
    (r) => r.file_name && /\.(png|jpe?g|webp)$/i.test(r.file_name as string),
  );
  return Promise.all(
    imgs.map(async (r): Promise<MockProductV2> => {
      // Thumbnail opt/ (300px) para que la grilla cargue rápido; fallback a
      // full/ si el opt no existe.
      let imageUrl = '';
      const optPath = `${companyId}/opt/${r.file_name}`;
      const { data: signed } = await supabase.storage
        .from(GENERADAS_BUCKET)
        .createSignedUrl(optPath, SIGNED_URL_TTL);
      if (signed?.signedUrl) {
        imageUrl = signed.signedUrl;
      } else {
        const fullPath = `${companyId}/full/${r.file_name}`;
        const { data: signedFull } = await supabase.storage
          .from(GENERADAS_BUCKET)
          .createSignedUrl(fullPath, SIGNED_URL_TTL);
        imageUrl = signedFull?.signedUrl || '';
      }
      return {
        id: `hist-${r.id}`,
        name: 'Imagen generada',
        imageUrl,
      };
    }),
  );
}

/** Una foto de un producto, con su vista (`frontal`, `trasera`, `lateral`, `detalle` o
 *  `null` si no tiene rótulo), firmada. */
export interface FotoDeProducto {
  vista: string | null;
  url: string;
}

/** Cuántas fotos por producto se le muestran al agente en `ver_productos`. Se queda en 4 aunque
 *  el tope por producto sea 5 (ADR-2026-09-29-tope-5-fotos-por-producto): cada foto viaja en
 *  el historial del chat en todos los turnos siguientes, y el agente decide con 4. */
export const MAX_VISTAS_A_VER = 4;

/**
 * Las fotos de VARIOS productos, con su vista, para que el agente sepa de qué lados hay
 * foto antes de planear poses — ADR-2026-09-13-el-agente-sabe-que-vistas-tiene-cada-producto.
 *
 * El motor sólo conoce la prenda por sus fotos: un producto con la frontal sola no tiene
 * espalda, y el agente pedía "de espaldas" sin saberlo. Mismo orden y mismo fallback de
 * firma que `fetchProductImageUrls` (principal primero, `full/` con caída a `opt/`), pero
 * para una lista de variantes en dos viajes en vez de dos por producto.
 */
export async function fetchProductVistas(
  companyId: string,
  variantIds: string[],
): Promise<Map<string, FotoDeProducto[]>> {
  const out = new Map<string, FotoDeProducto[]>();
  if (variantIds.length === 0) return out;
  const { data } = await supabase
    .from('productos_imagenes')
    .select('variante_id, url, vista, principal, orden')
    .in('variante_id', variantIds)
    .eq('id_company', companyId)
    .order('principal', { ascending: false })
    .order('orden', { ascending: true });
  const filas = ((data ?? []) as Array<{
    variante_id?: string | null;
    url?: string | null;
    vista?: string | null;
  }>).filter((f) => !!f.variante_id && !!f.url);

  // Path completo como clave: dos variantes pueden tener un archivo con el mismo nombre.
  const pathDe = (f: { variante_id?: string | null; url?: string | null }, tamano: 'full' | 'opt') =>
    `${companyId}/${f.variante_id}/${tamano}/${f.url}`;
  const aFirmar = filas.filter((f) => !(f.url as string).startsWith('http'));
  // Los paths ya traen el tamaño: la firma no necesita saberlo.
  const firmar = async (paths: string[]) => {
    const res = new Map<string, string>();
    if (paths.length === 0) return res;
    const { data: firmadas } = await supabase.storage
      .from(PRODUCTOS_BUCKET)
      .createSignedUrls(paths, SIGNED_URL_TTL);
    for (const item of firmadas ?? []) {
      if (item.path && item.signedUrl) res.set(item.path, item.signedUrl);
    }
    return res;
  };
  const enFull = await firmar(aFirmar.map((f) => pathDe(f, 'full')));
  const enOpt = await firmar(
    aFirmar.filter((f) => !enFull.has(pathDe(f, 'full'))).map((f) => pathDe(f, 'opt')),
  );

  for (const f of filas) {
    const id = f.variante_id as string;
    const lista = out.get(id) ?? [];
    if (lista.length >= MAX_VISTAS_A_VER) continue;
    const url = (f.url as string).startsWith('http')
      ? (f.url as string)
      : (enFull.get(pathDe(f, 'full')) ?? enOpt.get(pathDe(f, 'opt')));
    if (!url) continue;
    lista.push({ vista: f.vista?.trim() ? f.vista.trim().toLowerCase() : null, url });
    out.set(id, lista);
  }
  return out;
}
